// Пара «тот же?» и склейка (005: FR-023 – FR-026; ADR-0017, research, Р5, Р9). Записи сами не
// склеиваются: сервис предлагает пару с рекомендацией и доводами, решает человек.
import { z } from '../kernel/zod.ts';
import { IdSchema, PersonRefSchema, TimestampSchema } from '../kernel/common.ts';
import { RecordKindSchema, RecordRefSchema } from './record.ts';

export const pairAnswers = ['same', 'different', 'unknown'] as const;
export type PairAnswer = (typeof pairAnswers)[number];
export const PairAnswerSchema = z.enum(pairAnswers);

export const pairAnswerLabels = {
  same: 'Тот же',
  different: 'Разные',
  unknown: 'Не знаю',
} as const satisfies Record<PairAnswer, string>;

/**
 * Признаки пары (FR-023, FR-024): совместимость инициалов, род фамилии, близость написания и
 * звучания, общая почта, общая организация, те же документы и разделы, соседи по строкам.
 */
export const pairSignals = [
  'initials_compatible',
  'initials_conflict',
  'gender_conflict',
  'spelling_close',
  'sound_alike',
  'same_email',
  'same_organization',
  'same_document',
  'same_section',
  'neighbours',
] as const;
export type PairSignal = (typeof pairSignals)[number];
export const PairSignalSchema = z.enum(pairSignals);

export const pairSignalLabels = {
  initials_compatible: 'инициалы совместимы',
  initials_conflict: 'инициалы разные',
  gender_conflict: 'разный род фамилии',
  spelling_close: 'близкое написание',
  sound_alike: 'одинаково звучит',
  same_email: 'одна почта',
  same_organization: 'одна организация',
  same_document: 'одни документы',
  same_section: 'один раздел',
  neighbours: 'одни соседи по строкам',
} as const satisfies Record<PairSignal, string>;

/** Довод: признак, «за» или «против», сильный ли (сильный «против» — пара не «тот же»). */
export const PairEvidenceSchema = z.object({
  signal: PairSignalSchema,
  direction: z.enum(['for', 'against']),
  strong: z.boolean(),
});
export type PairEvidence = z.infer<typeof PairEvidenceSchema>;

/** Кто предложил пару: сервис при связывании, человек из карточки, агент по MCP. */
export const pairOrigins = ['system', 'user', 'agent'] as const;
export type PairOrigin = (typeof pairOrigins)[number];
export const PairOriginSchema = z.enum(pairOrigins);

export const PairSchema = z.object({
  id: IdSchema,
  kind: RecordKindSchema,
  a: RecordRefSchema,
  b: RecordRefSchema,
  /** Оценка сходства 0…1; пара предлагается от порога directory.pairThreshold. */
  score: z.number().min(0).max(1),
  recommendation: PairAnswerSchema,
  evidence: z.array(PairEvidenceSchema),
  origin: PairOriginSchema,
  answer: PairAnswerSchema.nullable(),
  answeredBy: PersonRefSchema.nullable(),
  answeredAt: TimestampSchema.nullable(),
  createdAt: TimestampSchema,
});
export type Pair = z.infer<typeof PairSchema>;

/** Сколько чего перенесла склейка на главную запись (FR-026, directory.merges.moved). */
export const MergeMovedSchema = z.object({
  mentions: z.number().int().min(0),
  aliases: z.number().int().min(0),
  emails: z.number().int().min(0),
  workplaces: z.number().int().min(0),
  curatorships: z.number().int().min(0),
});
export type MergeMoved = z.infer<typeof MergeMovedSchema>;

export const MergeSchema = z.object({
  id: IdSchema,
  main: RecordRefSchema,
  merged: RecordRefSchema,
  moved: MergeMovedSchema,
  at: TimestampSchema,
  by: PersonRefSchema,
  undoneAt: TimestampSchema.nullable(),
});
export type Merge = z.infer<typeof MergeSchema>;
